import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { ComercialLayout } from '../../components/comercial/ComercialLayout';
import { DocumentosCliente } from '../../components/comercial/DocumentosCliente';
import { InfoComercialCliente } from '../../components/comercial/InfoComercialCliente';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { toast } from '../../components/ui/sonner';
import axios from 'axios';
import {
  ArrowLeft,
  Building2,
  Home,
  User,
  Phone,
  Mail,
  MapPin,
  Calendar,
  Clock,
  Edit2,
  Save,
  X,
  MessageSquare,
  PhoneCall,
  FileText,
  Beaker,
  CheckCircle,
  AlertCircle,
  ChevronRight,
  Plus,
  Wrench,
  Shield,
  CreditCard,
  Package,
  Briefcase
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Stage labels and colors
const STAGE_CONFIG = {
  contacto_nuevo: { label: 'Contacto Nuevo', color: 'bg-sky-500/20 text-sky-400 border-sky-500/30' },
  interesado: { label: 'Interesado', color: 'bg-blue-500/20 text-blue-400 border-blue-500/30' },
  pendiente_instalacion_prueba: { label: 'Pdte. Instalación Prueba', color: 'bg-amber-500/20 text-amber-400 border-amber-500/30' },
  prueba_activa: { label: 'Prueba Activa', color: 'bg-purple-500/20 text-purple-400 border-purple-500/30' },
  pendiente_contrato: { label: 'Pendiente Contrato', color: 'bg-orange-500/20 text-orange-400 border-orange-500/30' },
  cliente: { label: 'Cliente', color: 'bg-emerald-500/20 text-emerald-400 border-emerald-500/30' },
  pendiente_retirada: { label: 'Pdte. Retirada', color: 'bg-red-500/20 text-red-400 border-red-500/30' },
  perdido: { label: 'Perdido', color: 'bg-zinc-500/20 text-zinc-400 border-zinc-500/30' },
};

// Timeline event icon mapping
const EVENT_ICONS = {
  creacion: CheckCircle,
  nota: MessageSquare,
  llamada: PhoneCall,
  visita: MapPin,
  email: Mail,
  conversion: Beaker,
  instalacion: Wrench,
  contrato: FileText,
  default: Clock
};

function formatDate(dateStr) {
  if (!dateStr) return '';
  const date = new Date(dateStr);
  return date.toLocaleDateString('es-ES', { day: 'numeric', month: 'short', year: 'numeric' });
}

function formatTime(dateStr) {
  if (!dateStr) return '';
  const date = new Date(dateStr);
  return date.toLocaleTimeString('es-ES', { hour: '2-digit', minute: '2-digit' });
}

function timeAgo(dateStr) {
  if (!dateStr) return '';
  const date = new Date(dateStr);
  const now = new Date();
  const diff = Math.floor((now - date) / 1000);
  
  if (diff < 60) return 'Hace un momento';
  if (diff < 3600) return `Hace ${Math.floor(diff / 60)} min`;
  if (diff < 86400) return `Hace ${Math.floor(diff / 3600)}h`;
  if (diff < 604800) return `Hace ${Math.floor(diff / 86400)} días`;
  return formatDate(dateStr);
}

// Tab Button Component
function TabButton({ active, onClick, icon: Icon, label, badge }) {
  return (
    <button
      onClick={onClick}
      className={`flex items-center gap-2 px-4 py-2.5 rounded-lg transition-all ${
        active 
          ? 'bg-white/10 text-white' 
          : 'text-zinc-400 hover:text-white hover:bg-white/5'
      }`}
    >
      <Icon className="w-4 h-4" />
      <span className="text-sm font-medium">{label}</span>
      {badge !== undefined && (
        <span className={`px-1.5 py-0.5 rounded text-[10px] ${active ? 'bg-white/20' : 'bg-white/10'}`}>
          {badge}
        </span>
      )}
    </button>
  );
}

// Data Row Component
function DataRow({ icon: Icon, label, value, editable, editing, onChange }) {
  if (editing && editable) {
    return (
      <div className="flex items-center gap-3 py-2">
        <Icon className="w-4 h-4 text-zinc-500 flex-shrink-0" />
        <span className="text-sm text-zinc-400 w-32">{label}</span>
        <Input
          value={value || ''}
          onChange={(e) => onChange(e.target.value)}
          className="flex-1 bg-white/[0.03] border-white/10 text-white h-8"
        />
      </div>
    );
  }
  
  return (
    <div className="flex items-center gap-3 py-2">
      <Icon className="w-4 h-4 text-zinc-500 flex-shrink-0" />
      <span className="text-sm text-zinc-400 w-32">{label}</span>
      <span className="text-sm text-white flex-1">{value || '-'}</span>
    </div>
  );
}

// Timeline Event Component
function TimelineEvent({ event, isLast }) {
  const Icon = EVENT_ICONS[event.tipo] || EVENT_ICONS.default;
  
  return (
    <div className="flex gap-4">
      <div className="flex flex-col items-center">
        <div className="w-8 h-8 rounded-full bg-white/10 flex items-center justify-center">
          <Icon className="w-4 h-4 text-zinc-400" />
        </div>
        {!isLast && <div className="w-px flex-1 bg-white/10 my-2" />}
      </div>
      <div className="flex-1 pb-6">
        <div className="flex items-center justify-between mb-1">
          <span className="text-sm font-medium text-white capitalize">{event.tipo.replace('_', ' ')}</span>
          <span className="text-xs text-zinc-500">{timeAgo(event.fecha)}</span>
        </div>
        <p className="text-sm text-zinc-400">{event.nota}</p>
        {event.usuario && (
          <p className="text-xs text-zinc-600 mt-1">Por: {event.usuario}</p>
        )}
      </div>
    </div>
  );
}

// Add Note Modal
function AddNoteModal({ onClose, onSave }) {
  const [tipo, setTipo] = useState('nota');
  const [nota, setNota] = useState('');
  const [saving, setSaving] = useState(false);

  const handleSave = async () => {
    if (!nota.trim()) {
      toast.error('Escribe una nota');
      return;
    }
    setSaving(true);
    await onSave(tipo, nota);
    setSaving(false);
    onClose();
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm">
      <div className="w-full max-w-md rounded-2xl bg-[#0a0a0a] border border-white/10 p-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-medium text-white">Añadir Seguimiento</h3>
          <button onClick={onClose} className="p-2 rounded-lg hover:bg-white/10">
            <X className="w-5 h-5 text-zinc-400" />
          </button>
        </div>
        
        <div className="space-y-4">
          <div>
            <label className="block text-sm text-zinc-400 mb-2">Tipo</label>
            <div className="flex gap-2">
              {[
                { value: 'nota', label: 'Nota', icon: MessageSquare },
                { value: 'llamada', label: 'Llamada', icon: PhoneCall },
                { value: 'visita', label: 'Visita', icon: MapPin },
                { value: 'email', label: 'Email', icon: Mail },
              ].map(t => (
                <button
                  key={t.value}
                  onClick={() => setTipo(t.value)}
                  className={`flex-1 flex items-center justify-center gap-2 py-2 rounded-lg border transition-all ${
                    tipo === t.value 
                      ? 'bg-sky-500/20 border-sky-500/50 text-sky-300' 
                      : 'border-white/10 text-zinc-400 hover:border-white/20'
                  }`}
                >
                  <t.icon className="w-4 h-4" />
                  <span className="text-xs">{t.label}</span>
                </button>
              ))}
            </div>
          </div>
          
          <div>
            <label className="block text-sm text-zinc-400 mb-2">Nota</label>
            <textarea
              value={nota}
              onChange={(e) => setNota(e.target.value)}
              placeholder="Escribe los detalles del seguimiento..."
              rows={4}
              className="w-full px-3 py-2 rounded-lg bg-white/[0.03] border border-white/10 text-white placeholder:text-zinc-600 text-sm resize-none"
            />
          </div>
        </div>
        
        <div className="flex justify-end gap-3 mt-6">
          <Button variant="outline" onClick={onClose} className="border-white/20 text-zinc-300">
            Cancelar
          </Button>
          <Button onClick={handleSave} disabled={saving} className="bg-sky-500 hover:bg-sky-600 text-white">
            {saving ? 'Guardando...' : 'Guardar'}
          </Button>
        </div>
      </div>
    </div>
  );
}

function ConvertirPruebaModal({ trialId, onClose, onSuccess }) {
  const [equipos, setEquipos] = useState([]);
  const [tecnicos, setTecnicos] = useState([]);
  const [equipo, setEquipo] = useState('pendiente_asesoramiento');
  const [tecnico, setTecnico] = useState('');
  const [fecha, setFecha] = useState('');
  const [hora, setHora] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [eqRes, tecRes] = await Promise.all([
          axios.get(`${API_URL}/api/comercial/equipos`, { withCredentials: true }),
          axios.get(`${API_URL}/api/comercial/tecnicos`, { withCredentials: true }),
        ]);
        setEquipos(eqRes.data || []);
        setTecnicos(tecRes.data || []);
      } catch (err) {
        console.error('Error cargando datos:', err);
      }
    };
    fetchData();
  }, []);

  const handleConvert = async () => {
    setSaving(true);
    try {
      await axios.post(
        `${API_URL}/api/comercial/contactos/${trialId}/convertir-prueba`,
        {
          equipo_recomendado: equipo,
          tecnico_id: tecnico || null,
          fecha_prevista: fecha || null,
          hora_prevista: hora || null,
        },
        { withCredentials: true }
      );
      toast.success('Contacto convertido en prueba gratuita');
      onSuccess();
      onClose();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error al convertir en prueba');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm">
      <div className="w-full max-w-md rounded-2xl bg-[#0a0a0a] border border-white/10 p-6" data-testid="convertir-prueba-modal">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-medium text-white">Convertir en Prueba Gratuita</h3>
          <button onClick={onClose} className="p-2 rounded-lg hover:bg-white/10">
            <X className="w-5 h-5 text-zinc-400" />
          </button>
        </div>

        <div className="space-y-4">
          <div>
            <label className="block text-sm text-zinc-400 mb-2">Equipo recomendado</label>
            <select
              value={equipo}
              onChange={(e) => setEquipo(e.target.value)}
              className="w-full px-3 py-2.5 rounded-lg bg-white/[0.03] border border-white/10 text-white text-sm"
              data-testid="convertir-equipo-select"
            >
              <option value="pendiente_asesoramiento" className="bg-zinc-900">Pendiente de asesoramiento</option>
              {equipos.map((eq) => (
                <option key={eq.id} value={eq.id} className="bg-zinc-900">
                  {eq.nombre}{eq.capacidad ? ` (${eq.capacidad})` : ''}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-sm text-zinc-400 mb-2">Técnico (opcional)</label>
            <select
              value={tecnico}
              onChange={(e) => setTecnico(e.target.value)}
              className="w-full px-3 py-2.5 rounded-lg bg-white/[0.03] border border-white/10 text-white text-sm"
              data-testid="convertir-tecnico-select"
            >
              <option value="" className="bg-zinc-900">Sin asignar</option>
              {tecnicos.map((t) => (
                <option key={t.id} value={t.id} className="bg-zinc-900">{t.name}</option>
              ))}
            </select>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-sm text-zinc-400 mb-2">Fecha prevista</label>
              <input
                type="date"
                value={fecha}
                onChange={(e) => setFecha(e.target.value)}
                className="w-full px-3 py-2 rounded-lg bg-white/[0.03] border border-white/10 text-white text-sm [color-scheme:dark]"
                data-testid="convertir-fecha-input"
              />
            </div>
            <div>
              <label className="block text-sm text-zinc-400 mb-2">Hora</label>
              <input
                type="time"
                value={hora}
                onChange={(e) => setHora(e.target.value)}
                className="w-full px-3 py-2 rounded-lg bg-white/[0.03] border border-white/10 text-white text-sm [color-scheme:dark]"
                data-testid="convertir-hora-input"
              />
            </div>
          </div>
        </div>

        <div className="flex justify-end gap-3 mt-6">
          <Button variant="outline" onClick={onClose} className="border-white/20 text-zinc-300">
            Cancelar
          </Button>
          <Button onClick={handleConvert} disabled={saving} className="bg-emerald-500 hover:bg-emerald-600 text-white" data-testid="convertir-confirmar-btn">
            {saving ? 'Convirtiendo...' : 'Convertir en Prueba'}
          </Button>
        </div>
      </div>
    </div>
  );
}

const TIPOS_ACTIVIDAD = [
  { value: 'llamar', label: 'Llamar', icon: PhoneCall },
  { value: 'visita', label: 'Visita', icon: MapPin },
  { value: 'whatsapp', label: 'WhatsApp', icon: MessageSquare },
  { value: 'enviar_presupuesto', label: 'Presupuesto', icon: FileText },
  { value: 'seguimiento', label: 'Seguimiento', icon: Clock },
  { value: 'recordatorio', label: 'Recordatorio', icon: AlertCircle },
];

function NuevaActividadModal({ trialId, onClose, onSuccess }) {
  const [tipo, setTipo] = useState('llamar');
  const [fecha, setFecha] = useState('');
  const [hora, setHora] = useState('');
  const [notas, setNotas] = useState('');
  const [saving, setSaving] = useState(false);

  const handleSave = async () => {
    if (!fecha) {
      toast.error('Selecciona la fecha de vencimiento');
      return;
    }
    setSaving(true);
    try {
      await axios.post(
        `${API_URL}/api/comercial/actividades`,
        { trial_id: trialId, tipo, fecha_vencimiento: fecha, hora: hora || null, notas: notas || null },
        { withCredentials: true }
      );
      toast.success('Actividad programada. Recibirás una notificación.');
      onSuccess();
      onClose();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error al crear la actividad');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm">
      <div className="w-full max-w-md rounded-2xl bg-[#0a0a0a] border border-white/10 p-6" data-testid="nueva-actividad-modal">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-medium text-white">Nueva Actividad</h3>
          <button onClick={onClose} className="p-2 rounded-lg hover:bg-white/10">
            <X className="w-5 h-5 text-zinc-400" />
          </button>
        </div>

        <div className="space-y-4">
          <div>
            <label className="block text-sm text-zinc-400 mb-2">Tipo de actividad</label>
            <div className="grid grid-cols-3 gap-2">
              {TIPOS_ACTIVIDAD.map((t) => (
                <button
                  key={t.value}
                  onClick={() => setTipo(t.value)}
                  className={`flex flex-col items-center gap-1.5 py-2.5 rounded-lg border transition-all ${
                    tipo === t.value
                      ? 'bg-sky-500/20 border-sky-500/50 text-sky-300'
                      : 'border-white/10 text-zinc-400 hover:border-white/20'
                  }`}
                  data-testid={`actividad-tipo-${t.value}`}
                >
                  <t.icon className="w-4 h-4" />
                  <span className="text-[10px]">{t.label}</span>
                </button>
              ))}
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-sm text-zinc-400 mb-2">Fecha *</label>
              <input
                type="date"
                value={fecha}
                onChange={(e) => setFecha(e.target.value)}
                className="w-full px-3 py-2 rounded-lg bg-white/[0.03] border border-white/10 text-white text-sm [color-scheme:dark]"
                data-testid="actividad-fecha-input"
              />
            </div>
            <div>
              <label className="block text-sm text-zinc-400 mb-2">Hora</label>
              <input
                type="time"
                value={hora}
                onChange={(e) => setHora(e.target.value)}
                className="w-full px-3 py-2 rounded-lg bg-white/[0.03] border border-white/10 text-white text-sm [color-scheme:dark]"
                data-testid="actividad-hora-input"
              />
            </div>
          </div>
          <div>
            <label className="block text-sm text-zinc-400 mb-2">Notas</label>
            <textarea
              value={notas}
              onChange={(e) => setNotas(e.target.value)}
              placeholder="Detalles de la actividad..."
              rows={3}
              className="w-full px-3 py-2 rounded-lg bg-white/[0.03] border border-white/10 text-white placeholder:text-zinc-600 text-sm resize-none"
              data-testid="actividad-notas-input"
            />
          </div>
        </div>

        <div className="flex justify-end gap-3 mt-6">
          <Button variant="outline" onClick={onClose} className="border-white/20 text-zinc-300">
            Cancelar
          </Button>
          <Button onClick={handleSave} disabled={saving} className="bg-sky-500 hover:bg-sky-600 text-white" data-testid="actividad-guardar-btn">
            {saving ? 'Guardando...' : 'Programar'}
          </Button>
        </div>
      </div>
    </div>
  );
}

// Pestañas que se pueden abrir directamente con ?tab= (p. ej. desde el checklist del CRM)
const TABS = ['datos', 'comercial', 'seguimiento', 'actividad', 'documentos', 'equipos'];

export default function FichaCliente() {
  const { trialId } = useParams();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { logout } = useAuth();
  const [trial, setTrial] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState(TABS.includes(searchParams.get('tab')) ? searchParams.get('tab') : 'datos');
  const [editing, setEditing] = useState(false);
  const [editData, setEditData] = useState({});
  const [nuevoEquipo, setNuevoEquipo] = useState({ numero_equipo: '', modelo: '', numero_serie: '' });

  const handleAddEquipo = async () => {
    if (!nuevoEquipo.modelo) {
      toast.error('Indica al menos el modelo del equipo');
      return;
    }
    try {
      await axios.put(`${API_URL}/api/comercial/trials/${trialId}`,
        { equipos: [...(trial.equipos || []), nuevoEquipo] }, { withCredentials: true });
      toast.success('Equipo registrado');
      setNuevoEquipo({ numero_equipo: '', modelo: '', numero_serie: '' });
      fetchTrial();
    } catch (err) {
      toast.error('Error al registrar el equipo');
    }
  };
  const [showNoteModal, setShowNoteModal] = useState(false);
  const [showConvertModal, setShowConvertModal] = useState(false);
  const [showActividadModal, setShowActividadModal] = useState(false);

  const fetchTrial = useCallback(async () => {
    try {
      const response = await axios.get(`${API_URL}/api/comercial/trials/${trialId}`, { withCredentials: true });
      setTrial(response.data);
      setEditData(response.data);
    } catch (err) {
      console.error('Error fetching trial:', err);
      toast.error('Error al cargar el cliente');
      navigate('/comercial/crm');
    } finally {
      setLoading(false);
    }
  }, [trialId, navigate]);

  useEffect(() => {
    fetchTrial();
  }, [fetchTrial]);

  const handleSave = async () => {
    try {
      // Solo enviar campos editables (evita duplicar seguimiento/checklist)
      const campos = ['empresa', 'nombre_contacto', 'telefono', 'email', 'direccion',
        'codigo_postal', 'ciudad', 'nif', 'direccion_fiscal', 'pais', 'tipo_negocio', 'observaciones'];
      const payload = {};
      campos.forEach((c) => { if (editData[c] !== undefined) payload[c] = editData[c]; });
      if (editData.iban) payload.iban = editData.iban; // solo si se introduce uno nuevo
      await axios.put(`${API_URL}/api/comercial/trials/${trialId}`, payload, { withCredentials: true });
      toast.success('Datos guardados');
      setEditing(false);
      fetchTrial();
    } catch (err) {
      toast.error('Error al guardar');
    }
  };

  const handleAddNote = async (tipo, nota) => {
    try {
      await axios.post(`${API_URL}/api/comercial/trials/${trialId}/seguimiento`, { tipo, nota }, { withCredentials: true });
      toast.success('Seguimiento añadido');
      fetchTrial();
    } catch (err) {
      toast.error('Error al añadir seguimiento');
    }
  };

  const handleConvertToPrueba = () => {
    setShowConvertModal(true);
  };

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#030303] flex items-center justify-center">
        <div className="w-8 h-8 border-2 border-white/20 border-t-white rounded-full animate-spin" />
      </div>
    );
  }

  if (!trial) return null;

  const stageConfig = STAGE_CONFIG[trial.crm_stage] || STAGE_CONFIG.contacto_nuevo;
  const isHogar = trial.tipo_cliente === 'hogar';
  const displayName = isHogar ? trial.nombre_contacto : (trial.empresa || trial.nombre_contacto);
  const canConvertToPrueba = ['contacto_nuevo', 'interesado'].includes(trial.crm_stage);
  const seguimiento = trial.seguimiento || [];
  const statusHistory = trial.status_history || [];
  const allTimeline = [...seguimiento, ...statusHistory.map(s => ({ ...s, tipo: 'estado', nota: s.note }))].sort((a, b) => new Date(b.fecha || b.date) - new Date(a.fecha || a.date));

  return (
    <ComercialLayout
      title="Ficha de Cliente"
      subtitle={`${trial.client_code} · ${displayName}`}
      onLogout={handleLogout}
      headerActions={
        <Button variant="outline" onClick={() => navigate('/comercial/crm')} className="border-white/20 text-zinc-300 hover:text-white">
          <ArrowLeft className="w-4 h-4 mr-2" />Volver al CRM
        </Button>
      }
    >
      {/* Header Card */}
      <div className="mb-6 p-6 rounded-2xl bg-white/[0.02] border border-white/[0.06]">
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
          <div className="flex items-center gap-4">
            <div className={`w-14 h-14 rounded-xl flex items-center justify-center ${isHogar ? 'bg-sky-500/20' : 'bg-amber-500/20'}`}>
              {isHogar ? <Home className="w-7 h-7 text-sky-400" /> : <Building2 className="w-7 h-7 text-amber-400" />}
            </div>
            <div>
              <div className="flex items-center gap-3 mb-1">
                <h2 className="text-xl font-medium text-white">{displayName}</h2>
                <span className={`px-2 py-1 rounded-full text-xs font-medium border ${stageConfig.color}`}>
                  {stageConfig.label}
                </span>
                {trial.crm_stage === 'prueba_activa' && trial.dias_en_prueba != null && (
                  <span className="px-2 py-1 rounded-full text-xs font-medium border bg-purple-500/15 text-purple-300 border-purple-500/30" data-testid="ficha-dia-prueba">
                    Día {trial.dias_en_prueba + 1} de prueba
                  </span>
                )}
              </div>
              <div className="flex items-center gap-4 text-sm text-zinc-400">
                <span className="font-mono">{trial.client_code}</span>
                {trial.comercial_name && <span>Comercial: {trial.comercial_name}</span>}
              </div>
            </div>
          </div>
          
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              onClick={() => setShowActividadModal(true)}
              className="border-white/20 text-zinc-300 hover:text-white"
              data-testid="ficha-nueva-actividad-btn"
            >
              <Clock className="w-4 h-4 mr-2" />Nueva Actividad
            </Button>
            {canConvertToPrueba && (
              <Button onClick={handleConvertToPrueba} className="bg-emerald-500 hover:bg-emerald-600 text-white" data-testid="ficha-convertir-prueba-btn">
                <Beaker className="w-4 h-4 mr-2" />Convertir en Prueba
              </Button>
            )}
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex gap-2 mb-6 overflow-x-auto pb-2">
        <TabButton active={activeTab === 'datos'} onClick={() => setActiveTab('datos')} icon={User} label="Datos" />
        <TabButton active={activeTab === 'comercial'} onClick={() => setActiveTab('comercial')} icon={Briefcase} label="Comercial" />
        <TabButton active={activeTab === 'seguimiento'} onClick={() => setActiveTab('seguimiento')} icon={MessageSquare} label="Seguimiento" badge={seguimiento.length} />
        <TabButton active={activeTab === 'actividad'} onClick={() => setActiveTab('actividad')} icon={Clock} label="Actividad" badge={allTimeline.length} />
        <TabButton active={activeTab === 'documentos'} onClick={() => setActiveTab('documentos')} icon={FileText} label="Documentos" />
        <TabButton active={activeTab === 'equipos'} onClick={() => setActiveTab('equipos')} icon={Package} label="Equipos" />
      </div>

      {/* Tab Content */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Main Content */}
        <div className="lg:col-span-2 space-y-6">
          {/* Datos Tab */}
          {activeTab === 'datos' && (
            <div className="p-6 rounded-2xl bg-white/[0.02] border border-white/[0.06]">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-sm font-medium text-white flex items-center gap-2">
                  <User className="w-4 h-4 text-zinc-500" />Información General
                </h3>
                {editing ? (
                  <div className="flex gap-2">
                    <Button variant="outline" size="sm" onClick={() => setEditing(false)} className="border-white/20 text-zinc-300">
                      <X className="w-4 h-4 mr-1" />Cancelar
                    </Button>
                    <Button size="sm" onClick={handleSave} className="bg-emerald-500 hover:bg-emerald-600 text-white">
                      <Save className="w-4 h-4 mr-1" />Guardar
                    </Button>
                  </div>
                ) : (
                  <Button variant="outline" size="sm" onClick={() => setEditing(true)} className="border-white/20 text-zinc-300">
                    <Edit2 className="w-4 h-4 mr-1" />Editar
                  </Button>
                )}
              </div>
              
              <div className="space-y-1 divide-y divide-white/[0.04]">
                <DataRow icon={isHogar ? Home : Building2} label="Tipo" value={isHogar ? 'Hogar' : 'Empresa'} />
                {!isHogar && (
                  <DataRow icon={Building2} label="Empresa" value={trial.empresa} editable editing={editing} onChange={(v) => setEditData({...editData, empresa: v})} />
                )}
                <DataRow icon={User} label="Contacto" value={trial.nombre_contacto} editable editing={editing} onChange={(v) => setEditData({...editData, nombre_contacto: v})} />
                <DataRow icon={Phone} label="Teléfono" value={trial.telefono} editable editing={editing} onChange={(v) => setEditData({...editData, telefono: v})} />
                <DataRow icon={Mail} label="Email" value={trial.email} editable editing={editing} onChange={(v) => setEditData({...editData, email: v})} />
                <DataRow icon={MapPin} label="Dirección" value={trial.direccion} editable editing={editing} onChange={(v) => setEditData({...editData, direccion: v})} />
                <DataRow icon={MapPin} label="Ciudad" value={`${trial.codigo_postal || ''} ${trial.ciudad || ''}`} />
                <DataRow icon={FileText} label="DNI / NIE / CIF" value={editing ? (editData.nif ?? trial.nif) : trial.nif} editable editing={editing} onChange={(v) => setEditData({...editData, nif: v})} />
                <DataRow icon={MapPin} label="Dirección fiscal" value={editing ? (editData.direccion_fiscal ?? trial.direccion_fiscal) : trial.direccion_fiscal} editable editing={editing} onChange={(v) => setEditData({...editData, direccion_fiscal: v})} />
                <DataRow icon={MapPin} label="País" value={editing ? (editData.pais ?? trial.pais ?? 'España') : (trial.pais || 'España')} editable editing={editing} onChange={(v) => setEditData({...editData, pais: v})} />
                <DataRow icon={Shield} label="IBAN (SEPA)" value={editing ? (editData.iban ?? '') : (trial.iban_masked || 'No registrado')} editable editing={editing} onChange={(v) => setEditData({...editData, iban: v})} />
                {editing && (
                  <p className="text-[10px] text-zinc-600 py-1">El IBAN se almacena cifrado y solo se usa para el Mandato SEPA y Odoo. Introduce el IBAN completo para actualizarlo.</p>
                )}
                {!isHogar && trial.tipo_negocio && (
                  <DataRow icon={Building2} label="Tipo negocio" value={trial.tipo_negocio} />
                )}
              </div>
            </div>
          )}

          {/* Comercial Tab */}
          {activeTab === 'comercial' && <InfoComercialCliente trialId={trialId} />}

          {/* Seguimiento Tab */}
          {activeTab === 'seguimiento' && (
            <div className="p-6 rounded-2xl bg-white/[0.02] border border-white/[0.06]">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-sm font-medium text-white flex items-center gap-2">
                  <MessageSquare className="w-4 h-4 text-zinc-500" />Seguimiento
                </h3>
                <Button size="sm" onClick={() => setShowNoteModal(true)} className="bg-sky-500 hover:bg-sky-600 text-white">
                  <Plus className="w-4 h-4 mr-1" />Añadir
                </Button>
              </div>
              
              {seguimiento.length === 0 ? (
                <div className="text-center py-8">
                  <MessageSquare className="w-10 h-10 text-zinc-700 mx-auto mb-3" />
                  <p className="text-sm text-zinc-500">Sin notas de seguimiento</p>
                  <Button size="sm" variant="outline" onClick={() => setShowNoteModal(true)} className="mt-4 border-white/20 text-zinc-300">
                    Añadir primera nota
                  </Button>
                </div>
              ) : (
                <div className="space-y-4">
                  {seguimiento.slice().reverse().map((event, idx) => (
                    <TimelineEvent key={idx} event={event} isLast={idx === seguimiento.length - 1} />
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Actividad Tab */}
          {activeTab === 'actividad' && (
            <div className="p-6 rounded-2xl bg-white/[0.02] border border-white/[0.06]">
              <h3 className="text-sm font-medium text-white flex items-center gap-2 mb-4">
                <Clock className="w-4 h-4 text-zinc-500" />Timeline de Actividad
              </h3>
              
              {allTimeline.length === 0 ? (
                <div className="text-center py-8">
                  <Clock className="w-10 h-10 text-zinc-700 mx-auto mb-3" />
                  <p className="text-sm text-zinc-500">Sin actividad registrada</p>
                </div>
              ) : (
                <div>
                  {allTimeline.map((event, idx) => (
                    <TimelineEvent key={idx} event={{...event, fecha: event.fecha || event.date}} isLast={idx === allTimeline.length - 1} />
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Documentos Tab */}
          {activeTab === 'documentos' && (
            <div className="p-6 rounded-2xl bg-white/[0.02] border border-white/[0.06]">
              <h3 className="text-sm font-medium text-white flex items-center gap-2 mb-4">
                <FileText className="w-4 h-4 text-zinc-500" />Documentación
              </h3>
              <DocumentosCliente trialId={trialId} />
            </div>
          )}

          {/* Equipos Tab */}
          {activeTab === 'equipos' && (
            <div className="p-6 rounded-2xl bg-white/[0.02] border border-white/[0.06]">
              <h3 className="text-sm font-medium text-white flex items-center gap-2 mb-4">
                <Package className="w-4 h-4 text-zinc-500" />Equipos
              </h3>

              {/* Equipos registrados con nº de serie (para documentos técnicos) */}
              <div className="space-y-2 mb-4" data-testid="ficha-equipos-lista">
                {(trial.equipos || []).map((eq, i) => (
                  <div key={i} className="flex items-center gap-3 p-3 rounded-xl bg-white/[0.03] border border-white/10">
                    <Package className="w-4 h-4 text-sky-400 flex-shrink-0" />
                    <div className="flex-1 text-sm text-white">
                      {eq.modelo}
                      <span className="text-xs text-zinc-500 ml-2">Nº {eq.numero_equipo || '-'} · S/N {eq.numero_serie || '-'}</span>
                    </div>
                  </div>
                ))}
                <div className="grid grid-cols-1 sm:grid-cols-4 gap-2 p-3 rounded-xl bg-white/[0.02] border border-dashed border-white/10">
                  <input
                    value={nuevoEquipo.numero_equipo}
                    onChange={(e) => setNuevoEquipo({ ...nuevoEquipo, numero_equipo: e.target.value })}
                    placeholder="Nº equipo"
                    className="px-3 py-2 rounded-lg bg-white/[0.03] border border-white/10 text-white text-sm placeholder:text-zinc-600"
                    data-testid="ficha-equipo-numero-input"
                  />
                  <input
                    value={nuevoEquipo.modelo}
                    onChange={(e) => setNuevoEquipo({ ...nuevoEquipo, modelo: e.target.value })}
                    placeholder="Modelo *"
                    className="px-3 py-2 rounded-lg bg-white/[0.03] border border-white/10 text-white text-sm placeholder:text-zinc-600"
                    data-testid="ficha-equipo-modelo-input"
                  />
                  <input
                    value={nuevoEquipo.numero_serie}
                    onChange={(e) => setNuevoEquipo({ ...nuevoEquipo, numero_serie: e.target.value })}
                    placeholder="Nº de serie"
                    className="px-3 py-2 rounded-lg bg-white/[0.03] border border-white/10 text-white text-sm placeholder:text-zinc-600"
                    data-testid="ficha-equipo-serie-input"
                  />
                  <Button size="sm" onClick={handleAddEquipo} className="bg-sky-500/20 text-sky-400 border border-sky-500/30 h-9" data-testid="ficha-equipo-add-btn">
                    <Plus className="w-4 h-4 mr-1" />Añadir
                  </Button>
                </div>
              </div>
              
              <div className="space-y-4">
                {trial.equipo_recomendado && (
                  <div className="p-4 rounded-xl bg-white/[0.02] border border-white/[0.06]">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-xs text-zinc-500 mb-1">Equipo Recomendado</p>
                        <p className="text-sm font-medium text-white">{trial.equipo_recomendado}</p>
                      </div>
                      <Shield className="w-5 h-5 text-amber-400" />
                    </div>
                  </div>
                )}
                
                {trial.equipo_instalado && (
                  <div className="p-4 rounded-xl bg-emerald-500/10 border border-emerald-500/20">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-xs text-emerald-400 mb-1">Equipo Instalado</p>
                        <p className="text-sm font-medium text-white">{trial.equipo_instalado}</p>
                        {trial.fecha_instalacion_definitiva && (
                          <p className="text-xs text-zinc-500 mt-1">Instalado: {formatDate(trial.fecha_instalacion_definitiva)}</p>
                        )}
                      </div>
                      <CheckCircle className="w-5 h-5 text-emerald-400" />
                    </div>
                  </div>
                )}
                
                {trial.proximo_mantenimiento && (
                  <div className="p-4 rounded-xl bg-white/[0.02] border border-white/[0.06]">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-xs text-zinc-500 mb-1">Próximo Mantenimiento</p>
                        <p className="text-sm font-medium text-white">{formatDate(trial.proximo_mantenimiento)}</p>
                      </div>
                      <Wrench className="w-5 h-5 text-blue-400" />
                    </div>
                  </div>
                )}
                
                {!trial.equipo_recomendado && !trial.equipo_instalado && (
                  <div className="text-center py-8">
                    <Package className="w-10 h-10 text-zinc-700 mx-auto mb-3" />
                    <p className="text-sm text-zinc-500">Sin equipos asignados</p>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>

        {/* Sidebar */}
        <div className="space-y-6">
          {/* Quick Info */}
          <div className="p-6 rounded-2xl bg-white/[0.02] border border-white/[0.06]">
            <h3 className="text-sm font-medium text-white mb-4">Resumen</h3>
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <span className="text-sm text-zinc-400">Estado</span>
                <span className={`px-2 py-1 rounded text-xs border ${stageConfig.color}`}>{stageConfig.label}</span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-sm text-zinc-400">Creado</span>
                <span className="text-sm text-white">{formatDate(trial.created_at)}</span>
              </div>
              {trial.origen_cliente && (
                <div className="flex items-center justify-between">
                  <span className="text-sm text-zinc-400">Origen</span>
                  <span className="text-sm text-white capitalize">{trial.origen_cliente.replace('_', ' ')}</span>
                </div>
              )}
              {trial.dias_en_prueba !== null && trial.dias_en_prueba !== undefined && (
                <div className="flex items-center justify-between">
                  <span className="text-sm text-zinc-400">Días en prueba</span>
                  <span className="text-sm text-purple-400">Día {trial.dias_en_prueba + 1}</span>
                </div>
              )}
            </div>
          </div>

          {/* Quick Actions */}
          <div className="p-6 rounded-2xl bg-white/[0.02] border border-white/[0.06]">
            <h3 className="text-sm font-medium text-white mb-4">Acciones Rápidas</h3>
            <div className="space-y-2">
              <Button variant="outline" className="w-full justify-start border-white/10 text-zinc-300 hover:text-white" onClick={() => setShowNoteModal(true)}>
                <MessageSquare className="w-4 h-4 mr-2" />Añadir Nota
              </Button>
              {trial.telefono && (
                <a href={`tel:${trial.telefono}`} className="flex items-center gap-2 w-full px-4 py-2 rounded-lg border border-white/10 text-zinc-300 hover:text-white hover:bg-white/5 transition-all text-sm">
                  <Phone className="w-4 h-4" />Llamar
                </a>
              )}
              {trial.email && (
                <a href={`mailto:${trial.email}`} className="flex items-center gap-2 w-full px-4 py-2 rounded-lg border border-white/10 text-zinc-300 hover:text-white hover:bg-white/5 transition-all text-sm">
                  <Mail className="w-4 h-4" />Enviar Email
                </a>
              )}
            </div>
          </div>

          {/* Generar Acuerdo Manklar - Preparado para futuro */}
          {trial.crm_stage === 'pendiente_contrato' && (
            <div className="p-6 rounded-2xl bg-gradient-to-br from-sky-500/10 to-purple-500/10 border border-sky-500/20">
              <h3 className="text-sm font-medium text-white mb-3 flex items-center gap-2">
                <FileText className="w-4 h-4 text-sky-400" />Documentación
              </h3>
              <p className="text-xs text-zinc-400 mb-4">
                Genera automáticamente todos los documentos necesarios para formalizar el contrato.
              </p>
              <Button 
                variant="outline" 
                className="w-full border-sky-500/30 text-sky-300 hover:bg-sky-500/20"
                disabled
                data-testid="generar-acuerdo-btn"
              >
                <FileText className="w-4 h-4 mr-2" />
                Generar Acuerdo Manklar
              </Button>
              <p className="text-[10px] text-zinc-600 mt-2 text-center">Próximamente: Acuerdo, SEPA, RGPD, Condiciones</p>
            </div>
          )}

          {/* Observaciones */}
          {trial.observaciones && (
            <div className="p-6 rounded-2xl bg-white/[0.02] border border-white/[0.06]">
              <h3 className="text-sm font-medium text-white mb-3">Observaciones</h3>
              <p className="text-sm text-zinc-400">{trial.observaciones}</p>
            </div>
          )}
        </div>
      </div>

      {/* Add Note Modal */}
      {showNoteModal && (
        <AddNoteModal onClose={() => setShowNoteModal(false)} onSave={handleAddNote} />
      )}
      {showConvertModal && (
        <ConvertirPruebaModal trialId={trialId} onClose={() => setShowConvertModal(false)} onSuccess={fetchTrial} />
      )}
      {showActividadModal && (
        <NuevaActividadModal trialId={trialId} onClose={() => setShowActividadModal(false)} onSuccess={fetchTrial} />
      )}
    </ComercialLayout>
  );
}
